import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldAlert, RotateCcw, ChevronDown, ChevronUp, Loader2 } from "lucide-react";

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "");

export function DeletedMessagesVault() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["admin-deleted-messages"], queryFn: async () => (await api.get("/admin/deleted-messages")).data, enabled: open });
  const restore = useMutation({
    mutationFn: (body) => api.post("/admin/deleted-messages/restore", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-deleted-messages"] }); toast.success("Restored — it is back in everyone's inbox"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const n = data ? data.threads.length + data.messages.length : null;
  return (
    <section className="mb-8 rounded-md border" data-testid="admin-deleted-messages">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-4 py-3 text-left" data-testid="admin-deleted-toggle">
        <ShieldAlert className="h-5 w-5 text-rose-600" />
        <span className="font-display text-lg font-extrabold">Deleted messages vault</span>
        <span className="text-sm text-muted-foreground">— nothing is ever lost; senders' "delete for everyone" can be reversed here{n !== null ? ` (${n})` : ""}</span>
        {open ? <ChevronUp className="ml-auto h-4 w-4" /> : <ChevronDown className="ml-auto h-4 w-4" />}
      </button>
      {open && (
        <div className="space-y-4 border-t px-4 py-3 text-sm">
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          {data && data.threads.length === 0 && data.messages.length === 0 && <p className="text-muted-foreground" data-testid="admin-deleted-empty">No deleted conversations or messages.</p>}
          {data?.threads.length > 0 && (
            <div>
              <h3 className="mb-1 font-semibold">Deleted conversations ({data.threads.length})</h3>
              <ul className="divide-y rounded-md border">
                {data.threads.map((t) => (
                  <li key={t.id} className="flex flex-wrap items-start gap-2 px-3 py-2" data-testid={`deleted-thread-${t.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{t.motel} · {t.participants.join(", ")} <span className="text-xs text-muted-foreground">({t.messages} message{t.messages === 1 ? "" : "s"})</span></div>
                      <div className="truncate text-xs text-muted-foreground">“{t.preview}”</div>
                      <div className="text-xs text-rose-700">Deleted by {t.deleted_by} · {when(t.deleted_at)} · started by {t.created_by}</div>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={restore.isPending} onClick={() => restore.mutate({ kind: "thread", id: t.id })} data-testid={`restore-thread-${t.id}`}>
                      <RotateCcw className="h-3.5 w-3.5" /> Restore
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data?.messages.length > 0 && (
            <div>
              <h3 className="mb-1 font-semibold">Deleted messages ({data.messages.length})</h3>
              <ul className="divide-y rounded-md border">
                {data.messages.map((m) => (
                  <li key={m.id} className="flex flex-wrap items-start gap-2 px-3 py-2" data-testid={`deleted-message-${m.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{m.motel} · {m.sender} → {m.participants.filter((p) => p !== m.sender).join(", ") || "—"}</div>
                      <div className="whitespace-pre-wrap text-xs">{m.text}</div>
                      <div className="text-xs text-rose-700">Sent {when(m.created_at)} · deleted by {m.deleted_by} · {when(m.deleted_at)}{m.thread_deleted ? " · whole conversation deleted" : ""}</div>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={restore.isPending} onClick={() => restore.mutate({ kind: "message", id: m.id })} data-testid={`restore-message-${m.id}`}>
                      <RotateCcw className="h-3.5 w-3.5" /> Restore
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
